// ABOUTME: Flow-based polling grid with horizontal origin-destination layout; integration status chips double as the third-party filter, entity chips scope to that selection
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  usePollingItems,
  useUpdatePolling,
  useUpdateConfig,
  useUpdateOperatorIntegration,
  useUpdateOperatorIntegrations,
} from '../../hooks/useAdminQueries';
import { LoadingCard } from './LoadingCard';
import { EditDrawer } from './EditDrawer';
import { PollingItem, Config } from '../../api/adminApi';
import { INTEGRATION_COLORS } from './integrationColors';
import { FilterChips } from './FilterChips';
import { DisableImpactChips } from './DisableImpactChips';
import { FlowDestinations } from './FlowDestinations';
import { IntegrationStatusBar } from './IntegrationStatusBar';
import { NotificationSettingsDrawer } from './NotificationSettingsDrawer';
import './PollingFlowGrid.css';

interface PollingFlowGridProps {
  operatorId: string;
  operatorName?: string;
}

interface ToggleModalData {
  type: 'polling' | 'config' | 'integration' | 'operator';
  id: string;
  name: string;
  currentState: boolean;
}

interface EditDrawerState {
  isOpen: boolean;
  type: 'polling' | 'config' | null;
  data: PollingItem | Config | null;
}

// The hub is one side of every flow, so it is never a filterable "other side".
const HUB = 'TECHLOG';

// The third parties a polling row touches: its origin plus every destination it
// reaches, minus the hub. Drives the integration filter (status chip selection).
function thirdPartiesOf(polling: PollingItem): string[] {
  const parties = new Set<string>([polling.origin, ...(polling.destinations || [])]);
  parties.delete(HUB);
  return [...parties];
}

export function PollingFlowGrid({ operatorId, operatorName }: PollingFlowGridProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading, error } = usePollingItems(operatorId);
  const updatePolling = useUpdatePolling();
  const updateConfig = useUpdateConfig();
  const updateOperatorIntegration = useUpdateOperatorIntegration();
  const updateOperatorIntegrations = useUpdateOperatorIntegrations();

  // Filters live in the URL query so a drill-down persists per session and is
  // shareable. Both dimensions are multi-select, comma-joined in their param.
  const readFilter = (param: string) =>
    (searchParams.get(param) || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
  const filterIntegrations = readFilter('integration');
  const filterEntities = readFilter('entity');

  const setFilter = (param: string, next: string[]) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next.length === 0) params.delete(param);
        else params.set(param, next.join(','));
        return params;
      },
      { replace: true },
    );
  };

  const toggleFilter = (param: string, selected: string[], value: string) => {
    setFilter(
      param,
      selected.includes(value)
        ? selected.filter((entry) => entry !== value)
        : [...selected, value],
    );
  };

  const toggleIntegration = (vendor: string) =>
    toggleFilter('integration', filterIntegrations, vendor);
  const toggleEntity = (value: string) =>
    toggleFilter('entity', filterEntities, value);

  // Selection state
  const [selectedPollingId, setSelectedPollingId] = useState<string | null>(null);

  // Modal state
  const [toggleModalData, setToggleModalData] = useState<ToggleModalData | null>(null);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [editDrawer, setEditDrawer] = useState<EditDrawerState>({
    isOpen: false,
    type: null,
    data: null,
  });
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const openEditDrawer = (type: 'polling' | 'config', data: PollingItem | Config) => {
    setEditDrawer({ isOpen: true, type, data });
  };

  const closeEditDrawer = () => {
    setEditDrawer({ isOpen: false, type: null, data: null });
  };

  // Handle toggle confirmation
  const handleToggleConfirm = async () => {
    if (!toggleModalData) return;
    setToggleLoading(true);
    
    try {
      if (toggleModalData.type === 'polling') {
        await updatePolling.mutateAsync({
          id: toggleModalData.id,
          data: { isDisabled: toggleModalData.currentState },
        });
      } else if (toggleModalData.type === 'integration') {
        await updateOperatorIntegration.mutateAsync({
          id: operatorId,
          integration: toggleModalData.id,
          isDisabled: toggleModalData.currentState,
        });
      } else if (toggleModalData.type === 'operator') {
        await updateOperatorIntegrations.mutateAsync({
          id: operatorId,
          isDisabled: toggleModalData.currentState,
        });
      } else {
        await updateConfig.mutateAsync({
          id: toggleModalData.id,
          data: { isDisabled: toggleModalData.currentState },
        });
      }
    } catch (error) {
      console.error('Failed to toggle status:', error);
    }
    
    setToggleLoading(false);
    setToggleModalData(null);
  };

  const formatTimeAgo = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (isLoading) {
    return (
      <div className="polling-flow-grid">
        <LoadingCard />
        <LoadingCard />
        <LoadingCard />
      </div>
    );
  }

  if (error) {
    return (
      <div className="polling-flow-error">
        <h3>Failed to load polling items</h3>
        <p>There was an error fetching polling data for this operator.</p>
        <button onClick={() => window.location.reload()} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  const pollingItems = data?.polling || [];

  if (pollingItems.length === 0) {
    return (
      <div className="polling-flow-empty">
        <h3>No polling items found</h3>
        <p>This operator doesn't have any polling items configured yet.</p>
      </div>
    );
  }

  // A row matches the integration selection when any of its third parties is
  // selected — so a shared fan-out row surfaces under every vendor it serves,
  // and both directions of an integration appear together.
  const integrationMatchedItems = pollingItems.filter((item) => {
    if (filterIntegrations.length === 0) return true;
    const parties = thirdPartiesOf(item);
    return filterIntegrations.some((vendor) => parties.includes(vendor));
  });

  // Entity options follow the integration selection so the chips only offer
  // entities the current sub-list can contain — offering one that cannot exist
  // for the selection is a dead end. With no integration selected the sub-list
  // is every row, so every entity is offered. An active entity stays offered
  // even when the selection cannot match it, so that dead-end can be cleared
  // in place.
  const entities = [
    ...new Set([
      ...integrationMatchedItems.map((p) => p.entity),
      ...filterEntities,
    ]),
  ].sort();

  const filteredItems = integrationMatchedItems.filter(
    (item) =>
      filterEntities.length === 0 || filterEntities.includes(item.entity),
  );

  const handleToggleConfig = (config: Config) => {
    setToggleModalData({
      type: 'config',
      id: config.id,
      name: `${config.destination} → ${config.entity}`,
      currentState: !config.isDisabled,
    });
  };

  const handleToggleIntegration = (integration: string, enabled: boolean) => {
    setToggleModalData({
      type: 'integration',
      id: integration,
      name: integration,
      currentState: enabled,
    });
  };

  const handleToggleAllIntegrations = (enabled: boolean) => {
    setToggleModalData({
      type: 'operator',
      id: operatorId,
      name: operatorName || operatorId,
      currentState: enabled,
    });
  };

  const handleEditConfig = (config: Config) => {
    openEditDrawer('config', config);
  };

  return (
    <div className="polling-flow-grid">
      {/* Per-integration status chips: the toggle disables/enables the whole
          integration, the chip body selects it as the third-party filter, and
          the master toggle disables/enables every integration at once */}
      <IntegrationStatusBar
        operatorId={operatorId}
        selected={filterIntegrations}
        onSelect={toggleIntegration}
        onToggle={handleToggleIntegration}
        onToggleAll={handleToggleAllIntegrations}
        onOpenNotifications={() => setNotificationsOpen(true)}
      />

      {/* Entity chips scope to the integration selection; the row always
          renders so it never vanishes as the selection narrows, and each
          dimension clears in place on its own chips */}
      <FilterChips
        label="ENTITIES"
        selected={filterEntities}
        options={entities}
        onToggle={toggleEntity}
        onClear={() => setFilter('entity', [])}
      />

      {/* Flow Layout */}
      <div className="flow-layout">
        <div className="flow-header">
          <span className="flow-title">Polling Items</span>
          <span className="flow-count">{filteredItems.length} items</span>
        </div>

        <div className="flow-list">
          {filteredItems.map((polling) => {
            const isSelected = selectedPollingId === polling.id;
            const originColor = INTEGRATION_COLORS[polling.origin] || '#3b82f6';
            const destinationsList = polling.destinations || [];
            // Number of destination flows (configs), which is what expanding the
            // row reveals — a vendor served for several operators counts once per
            // flow, so this can exceed the distinct-vendor count.
            const destinationCount = polling.configsCount || destinationsList.length;
            const peerFlows = polling.peerFlowsCount || 0;
            // The hover explains the "+N" in flows, naming any peer-operator
            // flows so the count is never read as extra vendors.
            const flowsTooltip =
              `${destinationCount} flow${destinationCount === 1 ? '' : 's'}: ${destinationsList.join(', ')}` +
              (peerFlows > 0
                ? ` (${peerFlows} for ${peerFlows === 1 ? 'another operator' : 'other operators'})`
                : '');

            return (
              <div
                key={polling.id}
                className={`flow-row ${isSelected ? 'expanded' : ''}`}
              >
                {/* Origin Card */}
                <div
                  className={`flow-origin ${isSelected ? 'selected' : ''} ${polling.isDisabled ? 'disabled' : ''}`}
                  style={{ '--card-color': originColor } as React.CSSProperties}
                  data-testid={`polling-${polling.id}`}
                  onClick={() => setSelectedPollingId(isSelected ? null : polling.id)}
                >
                  <div className="flow-origin-top">
                    <div
                      className="flow-origin-info"
                      data-testid={`polling-destinations-${polling.id}`}
                    >
                      <span className="flow-origin-name" style={{ color: originColor }}>
                        {polling.origin}
                      </span>
                      <span className="flow-origin-arrow">→</span>
                      <span className="flow-origin-entity">{polling.entity}</span>
                      <span className="flow-origin-arrow">→</span>
                      {destinationsList.length === 0 ? (
                        <span className="flow-origin-dest-empty">No destinations</span>
                      ) : (
                        <>
                          <span
                            className="dest-chip"
                            style={{ '--chip-color': INTEGRATION_COLORS[destinationsList[0]] } as React.CSSProperties}
                          >
                            {destinationsList[0]}
                          </span>
                          {destinationCount > 1 && (
                            <span
                              className="dest-chip-more"
                              title={flowsTooltip}
                              data-testid={`polling-dest-more-${polling.id}`}
                            >
                              +{destinationCount - 1}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flow-origin-actions">
                    <button
                      className="flow-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditDrawer('polling', polling);
                      }}
                    >
                      Edit
                    </button>
                    <div className="flow-origin-controls">
                      <span className="flow-origin-time">{formatTimeAgo(polling.lastSync)}</span>
                      <button
                        className={`flow-toggle ${polling.isDisabled ? 'off' : 'on'}`}
                        data-testid={`polling-toggle-${polling.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setToggleModalData({
                            type: 'polling',
                            id: polling.id,
                            name: `${polling.origin} → ${polling.entity}`,
                            currentState: !polling.isDisabled,
                          });
                        }}
                      >
                        <span className="flow-toggle-track">
                          <span className="flow-toggle-thumb" />
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Destinations - fetched when selected */}
                {isSelected && (
                  <FlowDestinations
                    polling={polling}
                    operatorId={operatorId}
                    onEditConfig={handleEditConfig}
                    onToggleConfig={handleToggleConfig}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Toggle Confirmation Modal */}
      {toggleModalData && (
        <div className="toggle-modal-overlay" onClick={() => !toggleLoading && setToggleModalData(null)}>
          <div className="toggle-modal" onClick={(e) => e.stopPropagation()}>
            <div className="toggle-modal-header">
              <h3 className="toggle-modal-title">
                {toggleModalData.currentState ? 'Disable' : 'Enable'}{' '}
                {
                  {
                    polling: 'Polling',
                    config: 'Config',
                    integration: 'Integration',
                    operator: 'All Integrations',
                  }[toggleModalData.type]
                }
                ?
              </h3>
            </div>
            <div className="toggle-modal-content">
              <p>
                Are you sure you want to <strong>{toggleModalData.currentState ? 'disable' : 'enable'}</strong> <strong>{toggleModalData.name}</strong>?
              </p>
              {toggleModalData.currentState && (
                toggleModalData.type === 'polling' ? (
                  <div className="toggle-modal-warning" data-testid="toggle-warning">
                    <DisableImpactChips pollingId={toggleModalData.id} operatorId={operatorId} />
                  </div>
                ) : toggleModalData.type === 'integration' ? (
                  <p className="toggle-modal-warning" data-testid="toggle-warning">
                    This will stop all active syncs for {operatorName || operatorId} on {toggleModalData.name}. Continue?
                  </p>
                ) : toggleModalData.type === 'operator' ? (
                  <p className="toggle-modal-warning" data-testid="toggle-warning">
                    This will stop all active syncs for {toggleModalData.name} across all integrations. Continue?
                  </p>
                ) : (
                  <p className="toggle-modal-warning" data-testid="toggle-warning">
                    This will stop syncing data for this config.
                  </p>
                )
              )}
            </div>
            <div className="toggle-modal-actions">
              <button
                className="toggle-modal-btn cancel"
                onClick={() => setToggleModalData(null)}
                disabled={toggleLoading}
              >
                Cancel
              </button>
              <button
                className={`toggle-modal-btn confirm ${toggleModalData.currentState ? 'danger' : 'success'}`}
                data-testid="toggle-confirm"
                onClick={handleToggleConfirm}
                disabled={toggleLoading}
              >
                {toggleLoading ? (
                  <span className="toggle-loading">
                    <span className="toggle-spinner" />
                    Processing...
                  </span>
                ) : (
                  toggleModalData.currentState ? 'Disable' : 'Enable'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Registry-driven edit drawer */}
      <EditDrawer
        isOpen={editDrawer.isOpen}
        type={editDrawer.type}
        data={editDrawer.data}
        onClose={closeEditDrawer}
      />

      <NotificationSettingsDrawer
        isOpen={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        operatorId={operatorId}
        operatorName={operatorName}
      />
    </div>
  );
}
